'use client';

import { useRef, useState } from 'react';
import { Heart, Minus, Plus, Search } from 'lucide-react';
import {
  FairtradeDarkPanelShell,
  fairtradeDarkInputClass,
} from '@/components/help-center/fairtrade/fairtrade-dark-panel';
import { DaysStarIcon } from '@/components/help-center/fairtrade/fairtrade-shared';
import { MoXat, cn } from '@/components/help-center/latest-power-shared';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePowerCollection } from '@/contexts/PowerCollectionContext';
import { type FairtradeQueryState } from '@/lib/fairtrade-query';

type FairtradeToolbarProps = {
  state: FairtradeQueryState;
  onChange: (patch: Partial<FairtradeQueryState>) => void;
  /** Total de powers no catálogo Fairtrade (denominador da lista de desejos). */
  catalogTotal: number;
  onOpenWishlist: () => void;
  /** Sem shell próprio — usado dentro do painel unificado com a lista. */
  embedded?: boolean;
  isDark?: boolean;
};

type PriceCurrency = 'xats' | 'days';

/** Amarelo suave do filtro de preço — sem saturação excessiva. */
const PRICE_YELLOW = {
  border: 'rgba(212, 175, 55, 0.42)',
  borderSoft: 'rgba(212, 175, 55, 0.28)',
  borderHover: 'rgba(234, 198, 90, 0.62)',
  glow: 'rgba(212, 175, 55, 0.12)',
  text: 'rgba(253, 230, 138, 0.88)',
  muted: 'rgba(253, 230, 138, 0.52)',
} as const;

function parseOptionalNumber(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function PriceStepperInput({
  id,
  label,
  value,
  currency,
  onChange,
}: {
  id: string;
  label: string;
  value: number | null;
  currency: PriceCurrency;
  onChange: (next: number | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const step = (delta: number) => {
    const base = value ?? 0;
    const next = Math.max(0, base + delta);
    onChange(next === 0 && value == null ? null : next);
  };

  const unit = currency === 'xats' ? 'xats' : 'days';
  const hasValue = value != null;
  const valueWidthCh = hasValue
    ? Math.min(Math.max(String(value).length + 0.75, 2.5), 10)
    : undefined;

  return (
    <div className="min-w-0 flex-1">
      <div
        className={cn(
          'group/price flex h-12 items-center gap-2 rounded-xl border border-solid px-2.5',
          'bg-[#140f05]/80',
          'shadow-[inset_0_1px_0_rgba(253,230,138,0.04)]',
          'transition-[border-color,box-shadow,background-color] duration-200',
          'hover:bg-[#1a1408]/90 hover:shadow-[0_0_0_1px_rgba(234,198,90,0.22),inset_0_1px_0_rgba(253,230,138,0.06)]',
          'focus-within:bg-[#1a1408]/95 focus-within:shadow-[0_0_0_2px_rgba(212,175,55,0.18)]'
        )}
        style={{ borderColor: PRICE_YELLOW.border }}
        onMouseEnter={(e) => {
          if (!e.currentTarget.contains(document.activeElement)) {
            e.currentTarget.style.borderColor = PRICE_YELLOW.borderHover;
          }
        }}
        onMouseLeave={(e) => {
          if (!e.currentTarget.contains(document.activeElement)) {
            e.currentTarget.style.borderColor = PRICE_YELLOW.border;
          }
        }}
        onFocusCapture={(e) => {
          e.currentTarget.style.borderColor = PRICE_YELLOW.borderHover;
        }}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            e.currentTarget.style.borderColor = PRICE_YELLOW.border;
          }
        }}
      >
        {currency === 'xats' ? (
          <MoXat className="h-4 w-4 shrink-0" />
        ) : (
          <DaysStarIcon className="h-4 w-4 shrink-0" />
        )}

        <div
          className="flex min-w-0 flex-1 cursor-text items-center"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget || (e.target as HTMLElement).dataset?.spacer) {
              e.preventDefault();
              inputRef.current?.focus();
            }
          }}
          onClick={() => inputRef.current?.focus()}
        >
          <label htmlFor={id} className="sr-only">
            {label}
          </label>
          <input
            ref={inputRef}
            id={id}
            type="number"
            min={0}
            inputMode="numeric"
            value={value ?? ''}
            onChange={(e) => onChange(parseOptionalNumber(e.target.value))}
            placeholder={label}
            style={valueWidthCh ? { width: `${valueWidthCh}ch` } : undefined}
            className={cn(
              'border-0 bg-transparent font-poppins text-sm font-semibold outline-none ring-0',
              'text-[#f5e6b8] placeholder:font-poppins placeholder:font-medium placeholder:text-[#e8d48a]/40',
              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
              hasValue ? 'shrink-0' : 'min-w-0 w-full flex-1'
            )}
          />
          {hasValue ? (
            <span className="pointer-events-none ml-0.5 shrink-0 select-none font-poppins text-sm font-semibold text-[#f5e6b8]">
              {unit}
            </span>
          ) : null}
          {hasValue ? (
            <span className="min-h-8 min-w-0 flex-1" data-spacer aria-hidden />
          ) : null}
        </div>

        <div
          className="ml-auto flex shrink-0 items-center gap-0.5 rounded-lg border border-solid bg-[#0c0903]/80 p-0.5"
          style={{ borderColor: PRICE_YELLOW.borderSoft }}
          role="group"
          aria-label={`Ajustar ${label}`}
        >
          <button
            type="button"
            onClick={() => step(-1)}
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-md',
              'text-[#e8d48a]/60 transition-all duration-200',
              'hover:bg-rose-400/20 hover:text-rose-100',
              'active:scale-95'
            )}
            aria-label={`Diminuir ${label}`}
          >
            <Minus className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-md',
              'text-[#e8d48a]/60 transition-all duration-200',
              'hover:bg-emerald-400/20 hover:text-emerald-100',
              'active:scale-95'
            )}
            aria-label={`Aumentar ${label}`}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FairtradeToolbar({
  state,
  onChange,
  catalogTotal,
  onOpenWishlist,
  embedded = false,
}: FairtradeToolbarProps) {
  const [currency, setCurrency] = useState<PriceCurrency>('xats');
  const { wishCount, ready } = usePowerCollection();

  const minKey = currency === 'xats' ? 'minXats' : 'minDays';
  const maxKey = currency === 'xats' ? 'maxXats' : 'maxDays';
  const minValue = currency === 'xats' ? state.minXats : state.minDays;
  const maxValue = currency === 'xats' ? state.maxXats : state.maxDays;

  const wishLabel = String(wishCount).padStart(2, '0');
  const totalLabel = String(Math.max(0, catalogTotal));

  const body = (
    <TooltipProvider delayDuration={250}>
      {/* Search + lista de desejos */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-3">
        <div
          className={cn(
            fairtradeDarkInputClass,
            'relative flex min-h-[4.5rem] min-w-0 flex-1 items-center gap-3 rounded-2xl px-4',
            'border-blue-900/60 bg-[#01012e]',
            'shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
            'focus-within:border-sky-400/45 focus-within:ring-2 focus-within:ring-sky-400/15'
          )}
        >
          <Search className="h-5 w-5 shrink-0 !text-sky-300/70" aria-hidden />
          <input
            id="fairtrade-search"
            type="search"
            value={state.query}
            onChange={(e) => onChange({ query: e.target.value, page: 1 })}
            placeholder="Buscar power por nome, categoria ou ID..."
            className={cn(
              'h-full min-h-[3.25rem] w-full border-0 bg-transparent py-3 outline-none ring-0',
              'font-poppins text-[15px] font-medium !text-sky-50',
              'placeholder:font-poppins placeholder:!text-blue-200/45',
              '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden'
            )}
            autoComplete="off"
            aria-label="Buscar power por nome, categoria ou ID..."
          />
        </div>

        <button
          type="button"
          onClick={onOpenWishlist}
          disabled={!ready}
          className={cn(
            'group relative min-h-[4.5rem] shrink-0 overflow-hidden rounded-2xl border border-solid text-left sm:min-w-[11rem]',
            'border-rose-400/40 bg-gradient-to-br from-rose-500/25 via-[#2a0a18] to-[#14040c]',
            'ring-1 ring-rose-400/25',
            'shadow-[0_0_24px_rgba(244,63,94,0.18),inset_0_1px_0_rgba(255,255,255,0.06)]',
            'transition-all duration-300 ease-out',
            'hover:border-rose-300/60 hover:from-rose-500/40 hover:via-[#3a1020] hover:to-[#1a0810]',
            'hover:ring-rose-300/40 hover:shadow-[0_0_36px_rgba(244,63,94,0.35),0_8px_24px_rgba(0,0,0,0.35)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/50',
            'active:scale-[0.99] disabled:opacity-60'
          )}
          aria-label={`Lista de desejos, ${wishCount} de ${catalogTotal} powers`}
        >
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-rose-400/15 via-transparent to-fuchsia-600/10 transition-opacity duration-300 group-hover:opacity-100"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{
              background:
                'radial-gradient(120% 80% at 80% 100%, rgba(251,113,133,0.35), transparent 55%)',
            }}
            aria-hidden
          />
          <Heart
            className={cn(
              'pointer-events-none absolute -bottom-7 -right-2 h-24 w-24 rotate-[12deg]',
              'fill-rose-400/25 stroke-rose-400/20 text-rose-400/25 sm:h-28 sm:w-28',
              'transition-[transform,fill,stroke] duration-300 ease-out',
              'group-hover:translate-y-1 group-hover:scale-105',
              'group-hover:fill-rose-700/40 group-hover:stroke-rose-800/25 group-hover:text-rose-700/40'
            )}
            strokeWidth={1.25}
            aria-hidden
          />
          <div className="relative z-10 flex h-full flex-col justify-center px-4 py-3 text-left sm:pr-12">
            <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-200/80 transition-colors group-hover:text-rose-100">
              Lista de desejos
            </p>
            <p className="mt-0.5 font-poppins text-xl font-bold tabular-nums leading-none text-rose-50">
              {wishLabel}
              <span className="text-sm font-medium text-rose-200/50">
                /{totalLabel}
              </span>
              <span className="ml-1.5 text-xs font-medium text-rose-200/50">
                {catalogTotal === 1 ? 'power' : 'powers'}
              </span>
            </p>
          </div>
        </button>
      </div>

      <div className="my-4 px-1">
        <PortalCrystalSeparator accentRgb="56, 189, 248" />
      </div>

      {/* Faixa de preço — amarelo suave */}
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-solid p-3.5 sm:p-4',
          'bg-gradient-to-br from-[#2a220e]/70 via-[#14100a] to-[#0c0a08]',
          'shadow-[0_0_24px_rgba(212,175,55,0.08),inset_0_1px_0_rgba(253,230,138,0.06)]'
        )}
        style={{ borderColor: PRICE_YELLOW.border }}
      >
        <div
          className="pointer-events-none absolute -right-6 -top-8 h-28 w-28 rounded-full blur-2xl"
          style={{ background: PRICE_YELLOW.glow }}
          aria-hidden
        />

        <div className="relative mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-xl">
            <div className="inline-flex items-center gap-2.5">
              {currency === 'xats' ? (
                <MoXat className="h-5 w-5 shrink-0 opacity-80" />
              ) : (
                <DaysStarIcon className="h-5 w-5 shrink-0 opacity-80" />
              )}
              <div>
                <h3
                  className="font-poppins text-base font-bold leading-none tracking-tight sm:text-lg"
                  style={{ color: PRICE_YELLOW.text }}
                >
                  Filtro de Preços
                </h3>
                <span
                  className="mt-1.5 block h-px w-10 rounded-full"
                  style={{
                    background:
                      'linear-gradient(to right, rgba(212,175,55,0.65), transparent)',
                  }}
                  aria-hidden
                />
              </div>
            </div>
            <p
              className="mt-2.5 font-poppins text-xs leading-relaxed sm:text-[13px]"
              style={{ color: PRICE_YELLOW.muted }}
            >
              Informe o valor mínimo e o valor máximo para listar só os powers nessa faixa.
              Alterne entre xats e days no seletor e use +/− para ajustar os
              valores.
            </p>
          </div>
          <div
            className="inline-flex items-center rounded-full border border-solid bg-[#120d04]/70 p-0.5"
            style={{ borderColor: PRICE_YELLOW.borderSoft }}
            role="group"
            aria-label="Moeda da faixa de preço"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setCurrency('xats')}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                    currency === 'xats'
                      ? 'bg-amber-400/15 ring-1 ring-amber-400/30'
                      : 'opacity-45 hover:opacity-85'
                  )}
                  aria-pressed={currency === 'xats'}
                  aria-label="Filtrar por xats"
                >
                  <MoXat className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-[220px] border-amber-500/25 bg-[#1a1408] font-poppins text-xs text-amber-50"
              >
                Filtrar por <strong>xats</strong>.
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setCurrency('days')}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                    currency === 'days'
                      ? 'bg-amber-400/15 ring-1 ring-amber-400/30'
                      : 'opacity-45 hover:opacity-85'
                  )}
                  aria-pressed={currency === 'days'}
                  aria-label="Filtrar por days"
                >
                  <DaysStarIcon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-[220px] border-amber-500/25 bg-[#1a1408] font-poppins text-xs text-amber-50"
              >
                Filtrar por <strong>days</strong>.
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="relative flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <PriceStepperInput
            id={`fairtrade-min-${currency}`}
            label="Mínimo"
            value={minValue}
            currency={currency}
            onChange={(next) => onChange({ [minKey]: next, page: 1 })}
          />

          <div className="hidden shrink-0 items-center sm:flex" aria-hidden>
            <span className="flex items-center">
              <span
                className="h-px w-4 rounded-full"
                style={{
                  background:
                    'linear-gradient(to right, transparent, rgba(212,175,55,0.45))',
                }}
              />
              <span
                className="mx-1.5 h-1.5 w-1.5 rotate-45 rounded-[1px]"
                style={{
                  backgroundColor: 'rgba(212, 175, 55, 0.7)',
                  boxShadow: '0 0 6px rgba(212, 175, 55, 0.3)',
                }}
              />
              <span
                className="h-px w-4 rounded-full"
                style={{
                  background:
                    'linear-gradient(to right, rgba(212,175,55,0.45), transparent)',
                }}
              />
            </span>
          </div>

          <PriceStepperInput
            id={`fairtrade-max-${currency}`}
            label="Máximo"
            value={maxValue}
            currency={currency}
            onChange={(next) => onChange({ [maxKey]: next, page: 1 })}
          />
        </div>
      </div>
    </TooltipProvider>
  );

  if (embedded) {
    return <div className="p-4 md:p-5">{body}</div>;
  }

  return <FairtradeDarkPanelShell innerClassName="p-4 md:p-5">{body}</FairtradeDarkPanelShell>;
}
